/**
 * 固件 BIN 文件解析器。
 *
 * 文件结构：
 * - byte 0-77: 固定 78 字节文件头
 * - byte 78 开始: app1Data[app_size]
 * - 随后: app2Data[APP2_size]
 * - 最后 4 字节: appDataCRC
 *
 * 所有 UInt16 / UInt32 均按小端序读取。
 */
class FirmwareBinParser {
  static HEADER_SIZE = 78;

  static CRC_SIZE = 4;

  /**
   * 读取浏览器选择的 BIN 文件并解析。
   *
   * @param {{arrayBuffer: () => Promise<ArrayBuffer>, name?: string}} file
   * @returns {Promise<ReturnType<typeof FirmwareBinParser.parse>>}
   */
  static async parseFile(file) {
    if (!file || typeof file.arrayBuffer !== 'function') {
      throw new TypeError('file 必须是浏览器 File 对象');
    }

    const result = FirmwareBinParser.parse(await file.arrayBuffer());
    return {
      ...result,
      fileName: file.name || '',
    };
  }

  /**
   * 解析固件 BIN 原始数据。
   *
   * @param {ArrayBuffer|Uint8Array} input
   * @returns {{
   *   raw: Uint8Array,
   *   header: {
   *     hexPID:number,
   *     timeUnix:number,
   *     pagesize:number,
   *     app_start:number,
   *     app_size:number,
   *     app_crc:number,
   *     app_version:number,
   *     table_start:number,
   *     table_size:number,
   *     table_crc:number,
   *     table_version:number,
   *     sd_start:number,
   *     sd_size:number,
   *     sd_crc:number,
   *     sd_version:number,
   *     APP2_start:number,
   *     APP2_size:number,
   *     APP2_crc:number,
   *     APP2_version:number,
   *     aesKeys:number[],
   *     nc:number
   *   },
   *   app1Data:Uint8Array,
   *   app2Data:Uint8Array,
   *   appDataCRC:number,
   *   calculatedAppDataCRC:number,
   *   crcData:Uint8Array,
   *   fileSize:number
   * }}
   */
  static parse(input) {
    const data = FirmwareBinParser.toUint8Array(input);
    const minimumSize = FirmwareBinParser.HEADER_SIZE + FirmwareBinParser.CRC_SIZE;

    if (data.length < minimumSize) {
      throw new RangeError(
        `BIN 文件至少需要 ${minimumSize} 字节，实际为 ${data.length} 字节`
      );
    }

    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    let offset = 0;
    const readUint32 = () => {
      const value = view.getUint32(offset, true);
      offset += 4;
      return value;
    };
    const readUint16 = () => {
      const value = view.getUint16(offset, true);
      offset += 2;
      return value;
    };

    const header = {
      hexPID: readUint32(),
      timeUnix: readUint32(),
      pagesize: readUint32(),
      app_start: readUint32(),
      app_size: readUint32(),
      app_crc: readUint32(),
      app_version: readUint16(),
      table_start: readUint32(),
      table_size: readUint32(),
      table_crc: readUint32(),
      table_version: readUint16(),
      sd_start: readUint32(),
      sd_size: readUint32(),
      sd_crc: readUint32(),
      sd_version: readUint32(),
      APP2_start: readUint32(),
      APP2_size: readUint32(),
      APP2_crc: readUint32(),
      APP2_version: readUint16(),
      aesKeys: Array.from(data.slice(offset, offset + 7)),
      nc: data[offset + 7],
    };
    offset += 8;

    if (offset !== FirmwareBinParser.HEADER_SIZE) {
      throw new Error(`BIN 文件头解析偏移错误：${offset}`);
    }

    const app1Start = FirmwareBinParser.HEADER_SIZE;
    const app1End = app1Start + header.app_size;
    const app2End = app1End + header.APP2_size;
    const crcOffset = app2End;
    const expectedFileSize = crcOffset + FirmwareBinParser.CRC_SIZE;

    if (!Number.isSafeInteger(expectedFileSize) || expectedFileSize > data.length) {
      throw new RangeError(
        `BIN 镜像长度超出文件范围：头部声明需要 ${expectedFileSize} 字节，实际为 ${data.length} 字节`
      );
    }
    if (expectedFileSize !== data.length) {
      throw new RangeError(
        `BIN 文件长度不匹配：头部声明应为 ${expectedFileSize} 字节，实际为 ${data.length} 字节`
      );
    }

    const appDataCRC = view.getUint32(crcOffset, true);
    const calculatedAppDataCRC = FirmwareBinParser.crc32Compute(data, crcOffset);
    if (calculatedAppDataCRC !== appDataCRC) {
      throw new Error(
        `BIN CRC 校验失败：文件值 0x${appDataCRC
          .toString(16)
          .padStart(8, '0')
          .toUpperCase()}，计算值 0x${calculatedAppDataCRC
          .toString(16)
          .padStart(8, '0')
          .toUpperCase()}`
      );
    }

    return {
      raw: data,
      header,
      app1Data: data.slice(app1Start, app1End),
      app2Data: data.slice(app1End, app2End),
      appDataCRC,
      calculatedAppDataCRC,
      // 按协议，最终 CRC 覆盖 byte0 到两个镜像数据结束的位置。
      crcData: data.slice(0, crcOffset),
      fileSize: data.length,
    };
  }

  /**
   * 计算协议使用的 CRC32。
   *
   * 等价于：
   * crc=0xFFFFFFFF，逐字节异或，使用 0xEDB88320 逐位右移，
   * 最后返回 (~crc) & 0xFFFFFFFF。
   *
   * @param {ArrayBuffer|Uint8Array} input
   * @param {number} [size] 从 byte0 开始参与计算的字节数
   * @returns {number} 无符号 32 位 CRC
   */
  static crc32Compute(input, size) {
    const data =
      input instanceof Uint8Array
        ? input
        : input instanceof ArrayBuffer
          ? new Uint8Array(input)
          : null;

    if (!data) {
      throw new TypeError('CRC 数据必须是 ArrayBuffer 或 Uint8Array');
    }

    const length = size === undefined ? data.length : size;
    if (!Number.isInteger(length) || length < 0 || length > data.length) {
      throw new RangeError(`CRC size 必须是 0-${data.length} 范围内的整数`);
    }

    let crc = 0xffffffff;
    for (let i = 0; i < length; i++) {
      crc = (crc ^ data[i]) >>> 0;
      for (let j = 0; j < 8; j++) {
        crc =
          (crc & 1) === 1
            ? ((crc >>> 1) ^ 0xedb88320) >>> 0
            : (crc >>> 1) >>> 0;
      }
    }
    return (~crc) >>> 0;
  }

  /**
   * @param {ArrayBuffer|Uint8Array} input
   * @returns {Uint8Array}
   */
  static toUint8Array(input) {
    if (input instanceof Uint8Array) {
      return input.slice();
    }
    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input.slice(0));
    }
    throw new TypeError('BIN 数据必须是 ArrayBuffer 或 Uint8Array');
  }
}

export default FirmwareBinParser;
